import { isSameDay, parseISO } from 'date-fns'
import z from 'zod'
import type { TimeSpan } from './time-entry-pause'
import {
  getSpanSeconds,
  splitAroundPauses,
  validatePauses,
} from './time-entry-pause'
import type { TimeEntryFormValues } from './types'

export interface TimeEntrySchemaOptions {
  requireComment: boolean
  departmentMandatory: boolean
  facilityMandatory: boolean
  /** Department and facility are filled in from the vehicle only. */
  vehicleRequired: boolean
}

export interface TimeEntrySchemaMessages {
  required: string
  vehicleRequired: string
  startBeforeEnd: string
  pauseOutside: string
  pauseOrder: string
  pauseOverlap: string
}

export function createTimeEntrySchema(
  options: TimeEntrySchemaOptions,
  messages: TimeEntrySchemaMessages
) {
  const lockedFieldError = options.vehicleRequired
    ? messages.vehicleRequired
    : messages.required

  return z
    .object({
      userId: z.string().min(1, messages.required),
      vehicleId: z.string(),
      start: z.date(),
      end: z.date(),
      pauses: z.array(z.object({ start: z.date(), end: z.date() })),
      facility: z.string(),
      department: z.string(),
      comment: z.string(),
      overtimeSalaryCodeId: z.string(),
      salaryCodeId: z.string(),
      diet: z.boolean(),
    })
    .superRefine((values, ctx) => {
      function fail(path: keyof TimeEntryFormValues, message: string) {
        ctx.addIssue({ code: 'custom', path: [path], message })
      }

      if (options.vehicleRequired && !values.vehicleId)
        fail('vehicleId', messages.vehicleRequired)
      if (options.departmentMandatory && !values.department)
        fail('department', lockedFieldError)
      if (options.facilityMandatory && !values.facility)
        fail('facility', lockedFieldError)
      if (options.requireComment && !values.comment.trim())
        fail('comment', messages.required)

      if (values.start > values.end) {
        fail('end', messages.startBeforeEnd)
        return
      }

      const pauseMessages = {
        outside: messages.pauseOutside,
        order: messages.pauseOrder,
        overlap: messages.pauseOverlap,
      }
      validatePauses(
        { start: values.start, end: values.end },
        values.pauses
      ).forEach((error, idx) => {
        if (!error) return
        ctx.addIssue({
          code: 'custom',
          path: ['pauses', idx, 'end'],
          message: pauseMessages[error],
        })
      })
    })
}

/** The time entries a registration turns into once its pauses are cut out. */
export function getTimeEntryFormSpans(
  values: Pick<TimeEntryFormValues, 'start' | 'end' | 'pauses'>
): TimeSpan[] {
  return splitAroundPauses(
    { start: values.start, end: values.end },
    values.pauses
  )
}

interface ExistingTimeEntry {
  datetimeStart: string
  datetimeEnd: string | null
}

/**
 * Whether the day's work, counting entries registered earlier that day, goes
 * past the daily limit once these entries are added.
 */
export function exceedsDailyWorkLimit(
  entries: TimeSpan[],
  existingEntries: ExistingTimeEntry[],
  dailyWorkLimit: number,
  now = new Date()
) {
  const first = entries.at(0)
  if (!first) return false

  const earlierThatDay = existingEntries
    .map(entry => ({
      start: parseISO(entry.datetimeStart),
      end: entry.datetimeEnd ? parseISO(entry.datetimeEnd) : now,
    }))
    .filter(
      entry => isSameDay(entry.start, first.start) && entry.start < first.start
    )

  return getSpanSeconds([...earlierThatDay, ...entries]) > dailyWorkLimit
}
